import { Controller } from "@hotwired/stimulus"

// Drag the bottom edge of a block in the week or day grid to change when it ends.
//
// Pointer events rather than HTML5 drag & drop: this gesture needs a continuous stream of
// positions to preview the new height, which dragover only gives coarsely, and the two
// mechanisms would fight over the same block otherwise.
const SNAP_MINUTES = 15
const MINUTES_PER_DAY = 1440
// An appointment always keeps some length, so the edge cannot be dragged above its start.
const MIN_MINUTES = 15

export default class extends Controller {
  static values = { url: String, view: String, viewDate: String }

  connect() {
    this.active = null
    this.onMove = this.move.bind(this)
    this.onUp = this.finish.bind(this)
  }

  disconnect() {
    this.detachListeners()
  }

  start(event) {
    const handle = event.currentTarget
    const block = handle.closest("[data-calendar-drag-target='chip']") || handle.parentElement
    const column = handle.closest("[data-drop-precision='time']")
    if (!column) return

    // Stop the click from opening the popover and the browser from starting its own drag.
    event.preventDefault()
    event.stopPropagation()

    this.active = {
      block,
      column,
      appointmentId: handle.dataset.appointmentId,
      date: handle.dataset.date,
      startMinutes: parseInt(handle.dataset.startsAtMinutes, 10) || 0,
      endMinutes: null,
    }
    block.classList.add("ring-2", "ring-green-500")
    handle.setPointerCapture?.(event.pointerId)

    document.addEventListener("pointermove", this.onMove)
    document.addEventListener("pointerup", this.onUp)
    document.addEventListener("pointercancel", this.onUp)
  }

  move(event) {
    if (!this.active) return

    const { column, block, startMinutes } = this.active
    const rect = column.getBoundingClientRect()
    if (!rect.height) return

    const raw = ((event.clientY - rect.top) / rect.height) * MINUTES_PER_DAY
    const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES
    const end = Math.min(Math.max(snapped, startMinutes + MIN_MINUTES), MINUTES_PER_DAY)

    this.active.endMinutes = end
    // Preview by resizing the block itself, so the gesture reads as direct manipulation.
    block.style.height = `${((end - startMinutes) / MINUTES_PER_DAY) * 100}%`
  }

  async finish() {
    const active = this.active
    this.detachListeners()
    this.active = null
    if (!active) return

    active.block.classList.remove("ring-2", "ring-green-500")
    if (active.endMinutes === null || !active.appointmentId) return

    const body = new FormData()
    body.append("date", active.date)
    body.append("precision", "resize")
    body.append("start_minutes", active.startMinutes)
    body.append("end_minutes", active.endMinutes)
    body.append("view", this.viewValue)
    body.append("view_date", this.viewDateValue)
    body.append("_method", "patch")

    const response = await fetch(this.urlValue.replace("__ID__", active.appointmentId), {
      method: "POST",
      body,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
      },
      credentials: "same-origin",
    })

    const html = await response.text()
    if (html) window.Turbo.renderStreamMessage(html)
  }

  detachListeners() {
    document.removeEventListener("pointermove", this.onMove)
    document.removeEventListener("pointerup", this.onUp)
    document.removeEventListener("pointercancel", this.onUp)
  }
}
